// IT 監控 › 事故（`kmgr-incidents` `/kmgr/incidents`，SPEC #kinio-kms-ui 導覽表）：未結（N）｜全部；
// 頂端「全部 / 沒人認領 / 我處理的」；排序照 #kinio-kms-incidents；清單底部〔再載入〕或「已顯示全部 N 件」。
// M1 最小版：批次列、匯出 CSV、自動處置紀錄分頁、家戶 / 等級 / 外部工單篩選留到 M2（k1-wbs M1-14 範圍）。
import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useScope } from '../../lib/scope';
import { SkeletonLines } from '../../components/Skeleton';
import { kmgrApi } from './api';
import { useKmgrDashboard, useKmgrEnv } from './hooks';
import { kmgrScopeOf, kmgrScopes, parseOrgFilter, sortIncidents, type IncidentFilter } from './model';
import { OrgFilter } from './OrgFilter';
import { Crumbs, errText, IncidentRow, LiveStamp, NS, StateBox } from './parts';

export function IncidentsPage() {
  const { t } = useTranslation(NS);
  const { me } = useKmgrEnv();
  const { planes, scope, orgs, visible } = useScope();
  const [sp, setSp] = useSearchParams();
  const tab = sp.get('tab') === 'all' ? 'all' : 'open';
  const f = (['all', 'unclaimed', 'mine'].includes(sp.get('f') ?? '') ? sp.get('f') : 'all') as IncidentFilter;
  const [conflict, setConflict] = useState<string | null>(null);
  const ks = kmgrScopeOf(scope);
  // 範圍「全部」時可再篩組織（多選 = 多個 org:<id>；M5-04）
  const orgF = ks.scope === 'all' ? parseOrgFilter(sp.get('org')) : [];
  const scopes = kmgrScopes(ks.scope, orgF);
  const api = planes[0]?.api ?? '';
  const dash = useKmgrDashboard();
  const q = useInfiniteQuery({
    queryKey: ['kmgr', 'incidents', api, ks.scope, orgF.join(','), tab, f],
    queryFn: ({ pageParam }) =>
      kmgrApi.incidents(api, {
        scope: scopes,
        // kmgr：handling 沒帶 = 還沒結案的三種；「全部」要明寫四種。claimed_by = user_id 或 none
        handling: tab === 'open' ? 'open,acknowledged,claimed' : 'open,acknowledged,claimed,resolved',
        claimed_by: f === 'unclaimed' ? 'none' : f === 'mine' ? me : undefined,
        cursor: pageParam || undefined,
        limit: 50,
      }),
    initialPageParam: '',
    getNextPageParam: (last) => last?.next_cursor || undefined,
    enabled: !!api && (f !== 'mine' || !!me),
    refetchInterval: 15_000,
  });
  const vis = new Set(visible.map((h) => h.home_id));
  const all = sortIncidents((q.data?.pages ?? []).flatMap((p) => p?.incidents ?? [])).filter((i) => !ks.clientFilter || vis.has(i.home_id));
  const crossOrg = new Set(all.map((i) => i.org_id)).size > 1 || orgs.filter((o) => o.kind === 'team').length > 1;
  const openN = dash.data?.incidents.counts.total;
  const set = (k: string, v: string | null) => {
    const n = new URLSearchParams(sp);
    if (v === null) n.delete(k);
    else n.set(k, v);
    setSp(n, { replace: true });
  };
  return (
    <section className="page km" data-testid="km-incidents-page">
      <Crumbs items={[{ label: t('nav.group'), to: '/kmgr/incidents' }, { label: t('nav.incidents') }]} />
      <div className="km-head">
        <h1 className="page-title">{t('nav.incidents')}</h1>
        <span className="spacer" />
        <LiveStamp updatedAt={q.dataUpdatedAt} stale={q.isError && !!q.data} />
      </div>
      <nav className="km-tabs" aria-label={t('incidents.tabs')}>
        <Link to="/kmgr/incidents" aria-current={tab === 'open' ? 'page' : undefined} onClick={() => setConflict(null)}>
          {openN === undefined ? t('incidents.tab_open') : t('incidents.tab_open_n', { n: openN })}
        </Link>
        <Link to="/kmgr/incidents?tab=all" aria-current={tab === 'all' ? 'page' : undefined} onClick={() => setConflict(null)}>
          {t('incidents.tab_all')}
        </Link>
      </nav>
      <div className="km-block-head">
        <div className="km-seg" role="group" aria-label={t('overview.filter')}>
          {(['all', 'unclaimed', 'mine'] as const).map((k) => (
            <button key={k} type="button" aria-pressed={f === k} onClick={() => set('f', k === 'all' ? null : k)}>
              {t(`filter.${k}`)}
            </button>
          ))}
        </div>
        <OrgFilter value={orgF} onChange={(v) => set('org', v.length ? v.join(',') : null)} />
      </div>
      {conflict && (
        <div className="km-bar warn" role="status">
          <span className="ic" aria-hidden>
            !
          </span>
          <span className="txt">{conflict}</span>
        </div>
      )}
      {q.isLoading ? (
        <div className="card">
          <SkeletonLines n={4} label={t('common.loading')} />
        </div>
      ) : q.isError && !q.data ? (
        <StateBox icon="⊘" title={t('incidents.unavailable')} body={errText(t, q.error)} testId="km-incidents-error">
          <button className="btn" onClick={() => void q.refetch()}>
            {t('common.retry')}
          </button>
        </StateBox>
      ) : all.length === 0 ? (
        f !== 'all' ? (
          <StateBox icon="○" title={t('incidents.none_filtered')} testId="km-incidents-empty">
            <button className="btn" onClick={() => set('f', null)}>
              {t('incidents.clear_filter')}
            </button>
          </StateBox>
        ) : (
          <StateBox icon="○" title={tab === 'open' ? t('incidents.none_open') : t('incidents.none')} testId="km-incidents-empty">
            {tab === 'open' && (
              <Link className="btn" to="/kmgr/incidents?tab=all">
                {t('incidents.see_all')}
              </Link>
            )}
          </StateBox>
        )
      ) : (
        <div className="card">
          <ul className="km-irows">
            {all.map((i) => (
              <IncidentRow key={i.incident_id} inc={i} me={me} crossOrg={crossOrg} onConflict={setConflict} />
            ))}
          </ul>
          <p className="muted km-sub" style={{ marginBottom: 0 }}>
            {q.hasNextPage ? (
              <button className="btn btn-sm" disabled={q.isFetchingNextPage} onClick={() => void q.fetchNextPage()}>
                {t('incidents.load_more')}
              </button>
            ) : (
              t('incidents.shown_all', { n: all.length })
            )}
          </p>
        </div>
      )}
    </section>
  );
}
